/**
 * 오늘출발(배포 2b) 화면 공통 판정 — DOM·API 없이 호출 가능한 순수 함수만 둔다.
 *
 * 검증: `node scripts/verify-today-delivery.js` (repo 관례 — `verify-holiday-rules.js` 와 같은 방식).
 */
import {
  APPLY_TYPE_ITEMS,
  PRODUCT_DELIVERY_FILTER_ITEMS,
  BULK_EXCEL_EXAMPLE_PREFIX,
  BULK_EXCEL_MAX_BYTES,
  BULK_EXCEL_MAX_ROWS,
  BULK_EXCEL_SHEET_TITLES,
  CUTOFF_MAX_HOUR,
  MAX_DELIVERY_CODE_OPTIONS,
  CUTOFF_MIN_HOUR,
  STOCK_MAX,
  TODAY_DELIVERY_SELLER_UI_MODE,
} from '@/constants/todayDelivery'
import {
  DELIVERY_CODE_LABEL,
  EXPOSE_STATE_LABEL,
  FAILURE_REASON,
  SOLDOUT_FAILURE_REASON,
  POLICY_SECTION,
  SOLDOUT_ACTION_LABEL,
  VALIDATION,
} from '@/constants/todayDeliveryMessages'

export type ApplyType = 'ALL' | 'PARTIAL' | 'NONE'
export type DeliverCode = '01' | '02' | '02p'
export type ExposureState = 'TODAY' | 'SOLDOUT' | 'NORMAL'
export type DeliveryCode = '01' | '02'
export type SoldoutAction = 'SOLD_OUT' | 'CONVERT_NORMAL'

/**
 * 지금 셀러어드민을 보는 사람이 마스터 계정인지 — `Layout` 이 `sellerInfo.accountType` 으로 옮기는 것과 같은 원천
 * (`localStorage.viewType`, 1 어드민 · 2 셀러). 저장소를 못 읽으면(차단·예외) 마스터가 아닌 것으로 본다.
 */
export function isTodayDeliveryMasterViewer(): boolean {
  try {
    return Number(localStorage.getItem('viewType')) === 1
  } catch {
    return false
  }
}

/**
 * 노출 모드(`TODAY_DELIVERY_SELLER_UI_MODE`)가 지금 보는 사람에게 오늘출발 UI 를 여는지 — 채널은 보지 않는다.
 * `'ALL'` 은 모두, `'MASTER'` 는 마스터 계정만, `'OFF'` 는 아무도. 옵션 관리 경로 가드(`router`)도 이 값을 쓴다.
 */
export function isTodayDeliveryUiOpenToViewer(): boolean {
  if (TODAY_DELIVERY_SELLER_UI_MODE === 'ALL') return true
  if (TODAY_DELIVERY_SELLER_UI_MODE === 'MASTER') return isTodayDeliveryMasterViewer()
  return false
}

/**
 * 오늘출발 UI 를 그릴지 — 하이버 서비스 AND 노출 모드(플랜 §8, PM 2026-10-02 마스터 선공개).
 * 둘 중 하나라도 아니면 섹션·컬럼·필터·메뉴가 렌더되지 않고 오늘출발 API 도 호출하지 않는다.
 * `'MASTER'` 모드의 일반 셀러는 이 값이 `'OFF'` 와 똑같이 false 다.
 */
export function isTodayDeliverySeller(channelCode: string | null | undefined): boolean {
  return channelCode === 'hiver' && isTodayDeliveryUiOpenToViewer()
}

/** 주문 마감시각 셀렉트 항목 — 09~23시 정시. */
export function cutoffItems(): { title: string; value: string }[] {
  const items: { title: string; value: string }[] = []
  for (let hour = CUTOFF_MIN_HOUR; hour <= CUTOFF_MAX_HOUR; hour += 1) {
    const value = `${String(hour).padStart(2, '0')}:00`
    items.push({ title: `${value} ${POLICY_SECTION.cutoffOptionSuffix}`, value })
  }
  return items
}

/** 서버가 준 `order_cutoff_time`("14:00:00" 또는 "14:00")을 셀렉트 값으로 맞춘다. */
export function normalizeCutoff(value: string | null | undefined): string | null {
  if (!value) return null
  const matched = /^(\d{2}):(\d{2})/.exec(String(value))
  return matched ? `${matched[1]}:${matched[2]}` : null
}

/** 컷오프 입력 오류 메시지 — 통과하면 빈 문자열. 서버 `TD_CUTOFF_INVALID` 와 같은 규칙. */
export function cutoffError(value: string | null | undefined): string {
  const normalized = normalizeCutoff(value)
  if (!normalized) return VALIDATION.cutoffRequired
  const [hour, minute] = normalized.split(':').map(Number)
  if (minute !== 0 || hour < CUTOFF_MIN_HOUR || hour > CUTOFF_MAX_HOUR) {
    return `주문 마감시각은 ${CUTOFF_MIN_HOUR}시부터 ${CUTOFF_MAX_HOUR}시까지 정시로 선택해 주세요.`
  }
  return ''
}

export function applyTypeLabel(value: string | null | undefined): string {
  return APPLY_TYPE_ITEMS.find((item) => item.value === value)?.title || '-'
}

/** 앱 노출 상태 3종 (adminapi `service.exposure_state`). */
export function exposureStateLabel(value: string | null | undefined): string {
  return EXPOSE_STATE_LABEL[value as keyof typeof EXPOSE_STATE_LABEL] || '-'
}

/** 배송타입(배송코드) 라벨 — 02 가 아니면 일반배송으로 본다(서버 기본값). */
export function deliveryCodeLabel(value: string | null | undefined): string {
  return value === '02' ? DELIVERY_CODE_LABEL['02'] : DELIVERY_CODE_LABEL['01']
}

/** 서버가 값을 안 주면 품절 처리(오픈 때 동작이 바뀌지 않게). */
export function normalizeSoldoutAction(value: string | null | undefined): SoldoutAction {
  return value === 'CONVERT_NORMAL' ? 'CONVERT_NORMAL' : 'SOLD_OUT'
}

/**
 * 옵션 bulk 에 실을 재고 소진 시 판매방식 값. 오늘출발(02)만 보낸다.
 * 일반배송(01)은 화면이 비활성이라 셀러가 고르지 않은 값 — 보내면 서버의 기존 값을 덮는다.
 */
export function soldoutActionForSave(
  deliveryCode: string | null | undefined,
  action: string | null | undefined
): { soldout_action: SoldoutAction } | Record<string, never> {
  if (deliveryCode !== '02') return {}
  return { soldout_action: normalizeSoldoutAction(action) }
}

/** 재고 소진 시 판매방식 선택지 — 배송정책·옵션 편집기 공통, 품절이 앞. */
export function soldoutActionItems(): { title: string; value: SoldoutAction }[] {
  return [
    { title: SOLDOUT_ACTION_LABEL.SOLD_OUT, value: 'SOLD_OUT' },
    { title: SOLDOUT_ACTION_LABEL.CONVERT_NORMAL, value: 'CONVERT_NORMAL' },
  ]
}

/**
 * 상품 리스트 행에서 **배송 구분**(`deliverCode`)을 읽는다.
 *
 * 리스트 API 는 adminapi 가 아니라 admin-api-v3 `GET /v2/products` 다([확인 2b-B]).
 * 그 레포가 아직 필드를 안 내려주는 동안(플래그 OFF·미배포)에도 컬럼이 깨지지 않게 `null` 을 돌려준다.
 */
export function readDeliverCode(item: Record<string, unknown> | null | undefined): DeliverCode | null {
  const raw = item?.deliverCode
  return raw === '01' || raw === '02' || raw === '02p' ? raw : null
}

/** 상품 리스트 배송 구분 라벨 — **필터 항목과 같은 단어**여야 셀러가 같은 것을 같게 읽는다. */
export function deliverCodeLabel(value: string | null | undefined): string {
  return PRODUCT_DELIVERY_FILTER_ITEMS.find((item) => item.value === value)?.title || '-'
}

/**
 * 배송코드 변경 실패 행 → 화면에 보일 번호·문구.
 * 응답 형태는 adminapi 구현(`{product_no | simple_product_no, reason_code}`)이 정본이다.
 */
export function failureRowNo(row: { product_no?: number | null; simple_product_no?: number | null }): number | null {
  return row.simple_product_no || row.product_no || null
}

export function failureRowMessage(row: { reason_code?: string }): string {
  return failureMessage(row.reason_code)
}

/** 재고 소진 시 판매방식 변경 실패 행 문구 — 이 맥락에서만 다르게 읽히는 사유(`SOLDOUT_FAILURE_REASON`)를 먼저 본다. */
export function soldoutFailureRowMessage(row: { reason_code?: string }): string {
  const own = SOLDOUT_FAILURE_REASON[row.reason_code as keyof typeof SOLDOUT_FAILURE_REASON]
  return own || failureMessage(row.reason_code)
}

/** 대상 SKU 합 상한 초과(adminapi 6ad42b2, 400) — 서버가 문구를 주지 않아 화면이 만든다 */
const TOO_MANY_OPTIONS = 'TD_TOO_MANY_OPTIONS'

/** 사유 코드 → 셀러 안내 문구 (adminapi `_reject_reason` 가 코드만 주므로 문구는 화면 몫이다). */
export function failureMessage(reasonCode: string | null | undefined): string {
  // 행 사유에는 상한 값이 따로 오지 않는다 — 기본 상한으로 채운다
  if (reasonCode === TOO_MANY_OPTIONS) return tooManyOptionsMessage()
  return FAILURE_REASON[reasonCode as keyof typeof FAILURE_REASON] || FAILURE_REASON.DEFAULT
}

/**
 * 대상 SKU 합 상한 초과 안내 — `{max}` 는 서버 `error_data.max_options` 를 천 단위 콤마로.
 * **양의 안전 정수(number)일 때만** 서버 값을 쓴다 — `true`·`0.5`·문자열·음수·NaN 등은 전부 기본 상한(40,000)이다
 * (`Number(true) > 0` 식 검사는 `true → 1개`, `0.5 → 0개` 를 만든다 — Astra W, cd1f9621).
 */
export function tooManyOptionsMessage(maxOptions?: unknown): string {
  const max = typeof maxOptions === 'number' && Number.isSafeInteger(maxOptions) && maxOptions > 0 ? maxOptions : MAX_DELIVERY_CODE_OPTIONS
  return FAILURE_REASON.TD_TOO_MANY_OPTIONS.replace('{max}', max.toLocaleString('ko-KR'))
}

/**
 * adminapi 쓰기 오류(`todayDeliveryError` 결과) → 셀러 안내. 서버가 문구를 주지 않는 `TD_TOO_MANY_OPTIONS`(400,
 * 메시지 "Bad Request")만 화면 문구로 바꾸고, 나머지는 서버 메시지 그대로(없으면 ''). 배송변경·옵션 지정/해제·
 * 옵션 편집기 저장이 같은 규칙을 쓴다.
 */
export function todayDeliveryErrorText(error: {
  error_code?: string
  error_data?: { max_options?: unknown; [key: string]: unknown } | null
  message?: string
}): string {
  if (error.error_code === TOO_MANY_OPTIONS) return tooManyOptionsMessage(error.error_data?.max_options)
  return error.message || ''
}

/** 다시 보내도 같은 답인 쓰기 오류 — 재시도 안내를 붙이지 않는다(상품을 나눠야 한다). */
export function isTooManyOptionsError(error: { error_code?: string }): boolean {
  return error.error_code === TOO_MANY_OPTIONS
}

/**
 * 다시 저장해도 같은 답인 실패를 **표시해서** 던진다 — 상품 저장 화면(`ProductDetailHiver.submit`)이 실패를 모아
 * 안내할 때 이 표시가 붙은 실패에는 "새로고침 후 다시 저장해 주세요" 를 붙이지 않는다(`isRetryableSaveFailure`).
 */
export function notRetryableError(message: string, errorCode: string): Error & { errorCode: string; retryable: false } {
  return Object.assign(new Error(message), { errorCode, retryable: false as const })
}

/**
 * 앞 단계는 저장됐고 뒤 단계만 실패한 저장(옵션 없는 상품: 판매정보 → 배송타입 → 판매방식)을 **표시해서** 던진다 —
 * 상품 저장 화면이 '저장 실패 · {항목}' 으로 묶지 않고 이 문장(무엇이 저장됐고 무엇이 안 됐는지 + 다시 저장 안내)만 알린다.
 */
export function partialSaveError(message: string): Error & { partialSaved: true } {
  return Object.assign(new Error(message), { partialSaved: true as const })
}

export function isPartialSaveFailure(reason: unknown): boolean {
  return (reason as { partialSaved?: unknown } | null | undefined)?.partialSaved === true
}

/** 저장 실패 사유를 다시 저장하면 달라질 수 있는지 — `notRetryableError` 로 표시한 것만 아니다(나머지는 기존처럼 재시도 대상). */
export function isRetryableSaveFailure(reason: unknown): boolean {
  return (reason as { retryable?: unknown } | null | undefined)?.retryable !== false
}

/**
 * 재고수량 입력 해석 — 옵션 관리 목록의 줄 수정과 [오늘출발 상품 추가하기] 가 같은 규칙을 쓴다.
 * - 재고관리를 안 하는 옵션은 빈칸으로 보여 주고(`stockInputText`), **숫자를 넣으면 재고관리로 바뀐다**
 *   (옵션 편집기의 재고수량 일괄 입력과 같은 규칙).
 * - 재고관리 중인 옵션을 빈칸으로 두면 잘못된 입력이다 — 이 화면에서는 재고관리를 끄지 않는다(끄는 곳은 옵션 편집기).
 * - 0~30,000 정수만(adminapi 옵션 재고 일괄수정과 같은 범위).
 */
export type StockEdit = { kind: 'unchanged' } | { kind: 'set'; qty: number } | { kind: 'invalid' }

export function stockEditOf(input: string | number | null | undefined, base: { managed: boolean; qty: number }): StockEdit {
  const text = String(input ?? '').trim()
  if (!text) return base.managed ? { kind: 'invalid' } : { kind: 'unchanged' }
  if (!/^\d+$/.test(text)) return { kind: 'invalid' }
  const qty = Number(text)
  if (qty > STOCK_MAX) return { kind: 'invalid' }
  if (base.managed && qty === Number(base.qty)) return { kind: 'unchanged' }
  return { kind: 'set', qty }
}

/** 재고수량 입력칸에 처음 채울 글자 — 재고관리를 안 하면 빈칸(placeholder 로 안내한다). */
export function stockInputText(base: { managed: boolean; qty: number }): string {
  return base.managed ? String(Number(base.qty) || 0) : ''
}

/**
 * 옵션 관리 목록 줄이 옵션 없는 상품인지 — 그 상품은 자기 행이 곧 SKU 라 옵션번호가 상품번호와 같다
 * (adminapi 목록 `COALESCE(SKU, 대표)`). 옵션 재고 일괄수정은 옵션 있는 상품만 받으므로 이 줄의 재고는 읽기 전용이다.
 */
export function isNoOptionItem(item: { simple_product_no?: number | null; product_no?: number | null }): boolean {
  return Boolean(item.product_no) && Number(item.simple_product_no) === Number(item.product_no)
}

/**
 * 옵션 관리 목록의 '오늘출발 적용일시'(`today_applied_at`, 오프셋 없는 KST) — 서버 글자를 그대로 잘라 `YYYY-MM-DD HH:mm` 으로 보인다.
 * Date·moment 로 읽지 않는다: 브라우저 현지 시각으로 해석하면 서머타임 지역에서 시각이 바뀐다(LA 에서 `2026-03-08T02:30:00` → 03:30).
 * 구분자는 'T'·공백 둘 다 받는다. 형식이 아니면(빈 값·null·필드 없음·다른 글자) '-'.
 */
export function todayAppliedAtText(value: string | null | undefined): string {
  const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(:\d{2})?/.exec(String(value ?? ''))
  return match ? `${match[1]} ${match[2]}` : '-'
}

/** 옵션 속성 값들을 ' / ' 로 이은 이름 — 값이 하나도 없으면(옵션 없는 상품) '-'. */
export function optionAttributesText(attributes: { value?: string | null }[] | null | undefined): string {
  const values = (attributes || []).map((attribute) => String(attribute?.value ?? '').trim()).filter(Boolean)
  return values.length ? values.join(' / ') : '-'
}

/** 상품번호 검색어를 공백·쉼표·줄바꿈으로 나눈 토큰 — 빈 토큰은 버리고 중복은 한 번만(서버도 중복을 빼고 센다). */
export function productNoTokens(keyword: string | null | undefined): string[] {
  return [
    ...new Set(
      String(keyword ?? '')
        .split(/[\s,]+/)
        .filter(Boolean)
    ),
  ]
}

/** 목록을 `size` 개씩 나눈다 — 한 번에 실을 수 있는 개수가 정해진 요청(재고 소진 시 판매방식 변경 500개)을 나눠 보낼 때 쓴다. */
export function chunksOf<T>(list: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let index = 0; index < list.length; index += size) chunks.push(list.slice(index, index + size))
  return chunks
}

/** 엑셀 일괄 지정 파일 사전 검증 (플랜 §3-9 수용 기준 — xlsx · 2MB · 5,000행). */
export function bulkFileError(file: { name?: string; size?: number } | null | undefined): string {
  if (!file) return VALIDATION.fileRequired
  if (!/\.xlsx$/i.test(file.name || '')) return VALIDATION.fileNotXlsx
  if ((file.size || 0) > BULK_EXCEL_MAX_BYTES) {
    return VALIDATION.fileTooLarge.replace('{mb}', String(BULK_EXCEL_MAX_BYTES / 1024 / 1024))
  }
  return ''
}

/**
 * 엑셀 일괄 지정 업로드에서 행을 셀 시트 이름 — 서버(adminapi `excel._data_sheet`)와 같은 순서:
 * `오늘출발 지정`(새 양식) → `오늘출발`(구 양식) → 활성 시트. 새 양식은 `작성 방법` 시트가 함께 있어
 * 첫 시트만 보면 셀러가 시트 순서를 바꾼 파일에서 안내 줄 수를 행 수로 센다.
 * 활성 시트는 SheetJS 가 `Workbook.WBView[0].activeTab`(모든 시트 기준 번호)으로 읽는다 — openpyxl `workbook.active` 와 같은 값.
 */
export function bulkDataSheetName(workbook: { SheetNames: string[]; Workbook?: unknown }): string | undefined {
  const named = BULK_EXCEL_SHEET_TITLES.find((title) => workbook.SheetNames.includes(title))
  if (named) return named
  const views = (workbook.Workbook as { WBView?: { activeTab?: number | string }[] } | undefined)?.WBView
  const active = Number(views?.[0]?.activeTab) || 0
  return workbook.SheetNames[active] ?? workbook.SheetNames[0]
}

/**
 * 옵션 관리 목록의 대표이미지 주소. adminapi `image_url` 은 `TB_C_PRODUCT_IMAGE.C_PRDUCT1_SMALL_URL` 의
 * **상대 경로**(`/cproduct/…`)라 그대로 쓰면 셀러어드민 도메인 기준으로 깨진다 — 이미지 도메인
 * (`VUE_APP_WEB_IMAGE_URL`, 다른 화면의 `${VUE_APP_WEB_IMAGE_URL}${key}` 와 같은 규약)을 앞에 붙인다.
 * `http(s)://` 로 시작하면 그대로, 비어 있으면 '' (화면이 이미지를 그리지 않는다).
 */
export function optionImageUrl(path: string | null | undefined): string {
  const value = String(path ?? '').trim()
  if (!value) return ''
  if (/^https?:\/\//i.test(value)) return value
  const base = String(process.env.VUE_APP_WEB_IMAGE_URL || '').replace(/\/+$/, '')
  return `${base}${value.startsWith('/') ? '' : '/'}${value}`
}

/**
 * 엑셀 일괄 지정 — 업로드 전에 세는 **데이터 행 수**. `rows` 는 시트를 배열로 읽은 것(첫 행 = 헤더, 빈 행 제외).
 * 서버 파서와 같은 규칙으로 센다: 헤더를 빼고, 새 양식의 예시 행(A열이 **문자열**이고 앞뒤 공백을 뗀 값이 `예시` 로 시작)은
 * 건너뛴다 — 5,000행 상한에도 세지 않는다. A열이 숫자(상품번호)인 행은 그대로 센다.
 */
export function bulkDataRowCount(rows: unknown[][]): number {
  return (rows || []).slice(1).filter((row) => {
    const first = row?.[0]
    return !(typeof first === 'string' && first.trim().startsWith(BULK_EXCEL_EXAMPLE_PREFIX))
  }).length
}

/** 엑셀 일괄 지정 — 헤더 밑의 **예시 행** 수(`bulkDataRowCount` 가 건너뛰는 행). 데이터 행이 0일 때 서버와 같은 두 문구를 가른다. */
export function bulkExampleRowCount(rows: unknown[][]): number {
  return (rows || []).slice(1).filter((row) => {
    const first = row?.[0]
    return typeof first === 'string' && first.trim().startsWith(BULK_EXCEL_EXAMPLE_PREFIX)
  }).length
}

/**
 * 업로드 전 행 수 검사 — 데이터 행이 없으면 서버(`TD_EXCEL_EMPTY`)와 같은 두 문구로 가른다:
 * 예시 행만 남았으면 `rowsExampleOnly`, 아예 없으면 `rowsEmpty`(PM 2026-10-01 ④).
 */
export function bulkRowCountError(rowCount: number, exampleRows = 0): string {
  if (rowCount <= 0) return exampleRows > 0 ? VALIDATION.rowsExampleOnly : VALIDATION.rowsEmpty
  if (rowCount > BULK_EXCEL_MAX_ROWS) return VALIDATION.rowsTooMany.replace('{max}', BULK_EXCEL_MAX_ROWS.toLocaleString('ko-KR'))
  return ''
}

/**
 * 옵션 SKU 대조 키 (2b-S4) — 옵션 저장 뒤 새로 생긴 SKU 에 화면에서 고른 배송타입을 다시 붙일 때 쓴다.
 *
 * 서버가 값을 다듬어 돌려줄 수 있으므로(앞뒤 공백·연속 공백·전각/반각·대소문자) **양쪽을 같은 규칙으로
 * 정규화**한 뒤 비교한다. 속성 순서도 서버 응답이 다를 수 있어 **정렬**한다 — 한 상품 안에서 축 집합은
 * 같으므로 정렬해도 서로 다른 SKU 가 같은 키가 되지 않는다.
 */
function normalizeAttr(value: string | null | undefined): string {
  return String(value ?? '')
    .normalize('NFKC')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
}

export function optionSignature(row: { attributes?: { title?: string; value?: string }[] } | null | undefined): string {
  return ((row && row.attributes) || [])
    .map((a) => `${normalizeAttr(a.title)}\u0001${normalizeAttr(a.value)}`)
    .sort()
    .join('\u0002')
}

/** 서명 → SKU 번호. 같은 서명이 둘 이상이면 **모호**로 표시해(0) 매칭 실패로 떨어뜨린다. */
export function buildSignatureIndex(
  rows: { product_id?: number; attributes?: { title?: string; value?: string }[] }[]
): Map<string, number> {
  const index = new Map<string, number>()
  for (const row of rows || []) {
    const key = optionSignature(row)
    index.set(key, index.has(key) ? 0 : Number(row.product_id) || 0)
  }
  return index
}

export interface PendingDeliveryCodes {
  /** `soldout_action` — 이번에 02 로 바뀌는 줄의 판매방식(편집기에서 고른 값). 없으면 서버가 셀러 기본값을 쓴다 */
  rows: { simple_product_no: number; delivery_code: DeliveryCode; soldout_action?: SoldoutAction }[]
  /** 저장 뒤에도 번호를 못 찾았거나 서명이 겹친 신규 SKU 수 — 0 이 아니면 **부분 저장하지 않는다** */
  unmatched: number
}

/**
 * 편집기 행에서 저장해야 할 배송타입만 골라 낸다 (2b-S4).
 * - 기존 SKU: 불러온 값과 다를 때만
 * - 신규 SKU(`product_id <= 0`): `signature → product_id` 로 번호를 채운다. **하나라도 못 찾으면
 *   `unmatched` 로 알린다** — 일부만 저장하고 성공으로 넘기지 않기 위해서다.
 */
export function pendingDeliveryCodes(
  rows: {
    product_id?: number
    delivery_code?: string
    delivery_code_base?: string
    soldout_action?: string
    soldout_action_base?: string
    attributes?: { title?: string; value?: string }[]
  }[],
  createdIdBySignature: Map<string, number> = new Map(),
  defaultSoldout?: string | null
): PendingDeliveryCodes {
  const pending: PendingDeliveryCodes['rows'] = []
  let unmatched = 0
  for (const row of rows || []) {
    const code = row.delivery_code === '02' ? '02' : '01'
    const isNew = !(Number(row.product_id) > 0)
    // 02 로 바뀌는 줄 가운데 판매방식을 처음 값과 다르게 고른 줄만 그 값을 보낸다 — 같으면 서버가 셀러 기본값을 쓴다(adminapi 7e50747).
    // 처음 값 = 불러온 값(`soldout_action_base`), 없으면(신규 SKU) 편집기 신규 기본값 — 편집기 storedSoldoutAction 과 같은 규칙
    const chosen = normalizeSoldoutAction(row.soldout_action)
    const changed = chosen !== normalizeSoldoutAction(row.soldout_action_base || defaultSoldout)
    const soldout = code === '02' && row.soldout_action && changed ? { soldout_action: chosen } : {}
    if (!isNew) {
      // 기존 SKU — 값이 그대로면 보낼 것이 없다
      if (row.delivery_code_base === code || !row.delivery_code) continue
      pending.push({ simple_product_no: Number(row.product_id), delivery_code: code, ...soldout })
      continue
    }
    if (!row.delivery_code) continue // 편집기에서 고르지 않은 신규 행 — 서버 기본값을 따른다
    const matched = createdIdBySignature.get(optionSignature(row)) || 0
    if (!matched) {
      unmatched += 1
      continue
    }
    // 신규 SKU 는 기준값이 없다 — 화면에서 고른 값을 그대로 확정한다
    pending.push({ simple_product_no: matched, delivery_code: code, ...soldout })
  }
  return { rows: pending, unmatched }
}

/**
 * 옵션 편집기 행이 '재고 필수' 인지 (PM 2026-10-01) — 재고 소진으로 일반배송 전환된 옵션(`soldout_converted`)을
 * 이번에 오늘출발로 바꾸면서 재고관리를 꺼 둔 채다. 서버(adminapi)가 이 지정을 `TD_STOCK_REQUIRED` 로 거절하므로
 * 편집기가 저장 전에 막는다(행 오류). 재고관리를 켜면(`inv_cnf_yn` Y) 풀린다 — 옵션 일괄저장이 배송타입 저장보다 먼저라
 * 서버는 재고관리가 켜진 뒤의 옵션을 본다(`PriceHiverComponent.flushOptionBulk`). 원래 재고관리를 안 쓰는 옵션은 그대로 허용한다.
 */
export function optionStockRequired(row: {
  soldout_converted?: boolean
  delivery_code?: string
  delivery_code_base?: string
  inv_cnf_yn?: string
}): boolean {
  return row.soldout_converted === true && row.delivery_code === '02' && row.delivery_code_base !== '02' && row.inv_cnf_yn === 'N'
}

/**
 * 옵션 없는 상품(상품 자체가 SKU)의 판매정보 배송타입 칸을 보일지 (PM 2026-10-01 ①).
 * 옵션 편집기의 배송타입 칸과 같은 게이트(하이버 · 오늘출발 UI 게이트)에, 국내배송 상품이고 지금 옵션을 쓰지 않으며
 * 판매정보를 읽을 때 현재 배송타입을 받아 둔(`todayDelivery`) 경우에만 — 등록·옵션 상품·해외배송·브랜디는 칸이 없다.
 */
export function noOptionTodayApplies(
  channelCode: string | null | undefined,
  sale: { useOption?: boolean; todayDelivery?: unknown } | null | undefined,
  deliveryType: string | null | undefined
): boolean {
  return isTodayDeliverySeller(channelCode) && !!sale?.todayDelivery && !sale.useOption && deliveryType !== 'OS'
}

/** 정책을 쓰지 않는 셀러의 새 지정(01→02) 거절 사유 — 행 실패와 엑셀 접수 409 가 같은 코드다(adminapi, PM 2026-10-13) */
export const TODAY_POLICY_OFF = 'TD_POLICY_OFF'

/**
 * 서버가 행 단위로 거절한 사유가 **다시 보내면 달라질 수 있는지**.
 * 소유·국내 여부·행 형식은 같은 답이 돌아오므로 큐에서 뺀다(무한 재시도 방지).
 * 중지(SUSPENDED)는 운영자가 풀면, 정책 미사용(`TD_POLICY_OFF`)은 셀러가 오늘출발을 시작하면 달라지므로 남긴다.
 */
export function isRetryableReason(reasonCode: string | null | undefined): boolean {
  return reasonCode === 'TD_SELLER_SUSPENDED' || reasonCode === TODAY_POLICY_OFF
}

/**
 * 오늘출발 정책이 활성인지 — ON 이고 참여 제한이 없다(adminapi `service.is_policy_active`·목록 `policy_active` 와 같은 판정).
 * 활성이 아니면 서버가 새 지정(01→02)을 `TD_POLICY_OFF`(참여 제한이면 `TD_PARTICIPATION_LOCKED`)로 거절한다(PM 2026-10-13).
 */
export function isTodayDeliveryPolicyActive(
  policy: { program_status?: string | null; participation_allowed?: boolean | null } | null | undefined
): boolean {
  return policy?.program_status === 'ON' && policy?.participation_allowed !== false
}
